// Main Character - Actor Swap's form (live's `MainCharacterActorSwapForm`, spec G14-templates):
// the talking-head clip, one to four actors each with its voice, and the Lip sync switch; one
// tile an actor.
import { Group, Stack, Text, Tooltip } from "@mantine/core";
import { AudioLines, SmilePlus, Speech } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { AddActorsDialog } from "../../../platform/actors/AddActorsDialog";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor } from "../../../platform/actors/picked";
import { useMakings, type Making } from "../../../platform/actors/use-makings";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { useVoicePages } from "../../../platform/voices/VoiceList";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { GlassPanel } from "../../../ui/GlassPanel";
import { Pill } from "../../../ui/Pill";
import {
  ReuseSwitch,
  StartArrow,
  VideoSlot,
  videoSource,
  type FormExample,
  type SlotVideo,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";
import classes from "./forms.module.css";
import { fromLibrary, libraryPick, recast, SwapCastRow, voiceOf, type SwapCast } from "./SwapCast";

/** Live's words (`constants.ts`). */
const TITLE = "Main Character - Actor Swap";
const MAX_ACTORS = 4;
const MAX_CLIP_MS = 15_000;
const CLIP_TIP =
  "Your source clip featuring a talking actor — this is the video whose main character will be " +
  "replaced.";
const CLIP_TOO_LONG =
  "Keep the clip under 15 seconds — a longer one is billed in full and only its first 15 " +
  "seconds are rendered.";
const LIP_SYNC_TIP =
  "Syncs the new actor's lips to the audio. Best used when the main actor is talking directly " +
  "on camera.";
const HANDOFF = "Your videos are finishing in My Generations — you can close this panel now.";

interface Values {
  video: SlotVideo | null;
  casts: SwapCast[];
  lipSync: boolean;
}

/**
 * The form's values from the card's example (live's `toMainCharacterExampleValues`): its hook,
 * each actor's portrait where it lies with its template voice, and its lip sync; none for another
 * template's.
 */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "main-character-actor-swap") return null;
  const { video, actors } = inputs;
  if (video.kind !== "example") return null;
  const voices = new Map(example.voices.map((voice) => [voice.id, voice]));
  const casts = actors.flatMap((each, at): SwapCast[] => {
    const voice = voices.get(each.voice_id);
    if (each.actor.kind !== "example" || voice === undefined) return [];
    const name = voice.name;
    return [
      {
        actor: {
          key: each.actor.key,
          name: `${name} (example ${at + 1})`,
          imageUrl: example.urls[each.actor.key] ?? null,
          thumbUrl: null,
          screenUrl: null,
          voice: { id: voice.id, name },
          videoUrl: null,
          source: each.actor,
        },
        picked: null,
      },
    ];
  });
  return {
    video: { exampleKey: video.key, url: example.urls[video.key] ?? null, name: "Example", durationMs: null },
    casts,
    lipSync: inputs.lip_sync ?? true,
  };
}

/**
 * Live's form: "Reuse example" (on fills the card's example: its hook, its three actors' portraits
 * with their template voices; off clears the form; Replicate opens it on), the media row ("Talking head",
 * From library or Upload, ringed with live's words past 15 seconds; then each actor's card with
 * its voice, the actors "Add AI Actor" is making, and "+ AI Actor": From Library opens Add Actors,
 * Add AI Actor the actor composer in the form's place), Voice Settings beside the panel for the
 * actor whose voice is being chosen, and in the footer live's Lip sync chip (on by default) and
 * the round start, off until the clip, an actor and each actor's voice are there and nothing is
 * being made. The run is the server's, so the panel says live's hand-off line once it started.
 */
export function ActorSwapForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const catalog = useGetCatalog().data;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [video, setVideo] = useState<SlotVideo | null>(start?.video ?? null);
  const [casts, setCasts] = useState<SwapCast[]>(start?.casts ?? []);
  const [lipSync, setLipSync] = useState(start?.lipSync ?? true);
  // Live's "Reuse example": on fills the card's example, off clears the form.
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setVideo(values?.video ?? null);
    setCasts(values?.casts ?? []);
    setLipSync(values?.lipSync ?? true);
    setEditing(null);
  };
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [makingActor, setMakingActor] = useState(false);
  const [makings, setMakings] = useState<Making[]>([]);
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      setCasts((current) => [...current, { actor: pickedOfActor(made), picked: null }]);
    },
    onFailed: (jobId) =>
      setMakings((current) =>
        current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each)),
      ),
  });
  // The actors' own voices, to play them and to offer them as "Recommended Voice".
  const globals = useVoicePages(teamId, brandId, { kind: "global" });
  const voices = globals.data?.pages.flatMap((page) => page.items) ?? [];
  // A voice's sample: the example's template voices (in no list), else the global voices'.
  const sampleOf = (voiceId: string) =>
    example?.voices.find((each) => each.id === voiceId)?.preview_url ??
    voices.find((each) => each.id === voiceId)?.preview_url ??
    null;
  const tooLong = (video?.durationMs ?? 0) > MAX_CLIP_MS;
  // Live's schema: the clip within 15 seconds, an actor, a voice for every actor, none in the making.
  const actors = casts.flatMap((cast) => {
    const voice = voiceOf(cast);
    return voice === null ? [] : [{ actor: cast.actor.source, voice_id: voice.id }];
  });
  const ready = video !== null && !tooLong && casts.length > 0 && makings.length === 0;
  const inputs =
    !ready || actors.length < casts.length
      ? null
      : {
          template: "main-character-actor-swap" as const,
          video: videoSource(video),
          actors,
          lip_sync: lipSync,
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const editingCast = casts.find((cast) => cast.actor.key === editing) ?? null;
  const recommended = voices.find((each) => each.id === editingCast?.actor.voice?.id) ?? null;
  if (makingActor && catalog !== undefined) {
    // Live hides the form while the composer is open; the form keeps its state.
    const close = () => setMakingActor(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(started) => {
          setMakings((current) => [...current, { jobId: started.id, failed: false }]);
          close();
        }}
        place={{
          // Live's template panel starts a new actor on Female (`DEFAULT_ACTOR_GENDER`).
          gender: "female",
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const room = MAX_ACTORS - casts.length - makings.length;
            const added = made.slice(0, room).map((each) => ({ actor: pickedOfActor(each), picked: null }));
            setCasts((current) => [...current, ...added]);
            close();
          },
        }}
      />
    );
  }
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      // Live locks it while an actor is being made (`!hasActorGenerations`): the example would
      // drop the card in the making.
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} locked={makings.length > 0} />}
      summary={<Text size="sm">{TITLE}</Text>}
      aside={
        editingCast === null ? undefined : (
          <VoicePanel
            key={editingCast.actor.key}
            teamId={teamId}
            brandId={brandId}
            chosen={editingCast.picked ?? recommended}
            recommended={recommended}
            designModel={catalog?.audio_models.find((each) => each.job_types.includes("voice.design"))}
            onChoose={(voice) =>
              setCasts((current) =>
                current.map((cast) => (cast.actor.key === editingCast.actor.key ? { ...cast, picked: voice } : cast)),
              )
            }
            onClose={() => setEditing(null)}
            head={<VoiceHead cast={editingCast} />}
          />
        )
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Tooltip label={LIP_SYNC_TIP} multiline maw={280} withArrow>
            <Pill
              label={lipSync ? "On" : "Off"}
              icon={<Speech size={16} />}
              active={lipSync}
              aria-pressed={lipSync}
              aria-label="Lip sync"
              onClick={() => setLipSync((on) => !on)}
            />
          </Tooltip>
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={casts.length} quote={job.lastPriced} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <div className={classes.mediaRow}>
          <VideoSlot
            teamId={teamId}
            brandId={brandId}
            label="Talking head"
            icon={<SmilePlus size={20} strokeWidth={1.5} aria-hidden />}
            subtitle="Pick the clip whose main character is replaced"
            tooltip={CLIP_TIP}
            value={video}
            onChange={setVideo}
            problem={tooLong ? CLIP_TOO_LONG : null}
            tile
          />
          <SwapCastRow
            casts={casts}
            sampleOf={sampleOf}
            makings={makings}
            full={casts.length + makings.length >= MAX_ACTORS}
            onRemove={(key) => {
              setCasts((current) => current.filter((cast) => cast.actor.key !== key));
              if (editing === key) setEditing(null);
            }}
            onVoice={setEditing}
            onFromLibrary={() => setAdding(true)}
            onMakeActor={
              catalog === undefined || actorModels(catalog.image_models).length === 0
                ? null
                : () => {
                    setEditing(null);
                    setMakingActor(true);
                  }
            }
            onDropMaking={(jobId) => setMakings((current) => current.filter((each) => each.jobId !== jobId))}
          />
        </div>
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts), and its hand-off line. */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started. {HANDOFF}
          </Text>
        ) : null}
      </Stack>
      {adding ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={casts.flatMap(libraryPick)}
          max={MAX_ACTORS - makings.length - casts.filter((cast) => !fromLibrary(cast)).length}
          onClose={() => setAdding(false)}
          onConfirm={(actors) => setCasts((current) => recast(current, actors))}
        />
      ) : null}
    </GlassPanel>
  );
}

/** Live's head of Voice Settings: the actor's portrait and its voice, or "Select Voice". */
function VoiceHead({ cast }: { cast: SwapCast }) {
  const voice = voiceOf(cast);
  return (
    <div className={classes.voiceHead}>
      {cast.actor.imageUrl === null ? null : <img className={classes.voiceHeadPortrait} src={cast.actor.imageUrl} alt="" />}
      {voice === null ? (
        <span className={classes.voiceHeadEmpty}>Select Voice</span>
      ) : (
        <span className={classes.voiceHeadName}>
          <AudioLines size={16} aria-hidden />
          <span>{voice.name}</span>
        </span>
      )}
    </div>
  );
}
